import React, { useState } from 'react';

import { Container, Nav, Navbar, NavDropdown } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import logo from '../../../assets/img/inside-logo.svg';

import LogoutModal from './LogoutModal';
import { useTranslation } from 'react-i18next';
import { COUNTRIES } from '@wareclouds/frontend-common';
import {
  useHasMissingBankDetails,
  useClouderHomePath,
  useIsRegionalHubClouder,
  useCanAccessBankAccount,
} from '../../../hooks';
import env from '../../../config/env';
import { IS_BR_CONTEXT } from '../../../config/consts/commons';
import { AlertComponent } from '../components';
import { useGetClouderStatus } from '../../routes/services';
import { IDENTITY_VALIDATION_STATUS } from '../../routes/enabled/utils/identityValidation';

const NavigationBar = () => {
  const { t } = useTranslation(['glossary']);
  const navigate = useNavigate();
  const { MessageHasNotBankData, isBankAccountRoute } =
    useHasMissingBankDetails();
  const { isRegionalHubClouder, isLoading } = useIsRegionalHubClouder();
  const { canAccessBankAccount, isLoading: isLoadingBankAccess } =
    useCanAccessBankAccount();
  const { homePath } = useClouderHomePath();
  const showStandardFeatures = !isLoading && !isRegionalHubClouder;
  const showBankAccount = !isLoadingBankAccess && canAccessBankAccount;
  const { username, isBlocked } = useSelector((state) => state.auth);
  const { data: clouderStatus } = useGetClouderStatus({
    enabled: IS_BR_CONTEXT,
  });
  const isIdentityRejected =
    IS_BR_CONTEXT &&
    Number(clouderStatus?.data?.id_user_status) ===
      IDENTITY_VALIDATION_STATUS.REJECTED;
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  return (
    <>
      <LogoutModal
        show={showLogoutModal}
        handleClose={() => setShowLogoutModal(false)}
      />

      <Navbar bg="primary" expand="lg" collapseOnSelect>
        <Container fluid>
          <Navbar.Brand as={Link} to={homePath}>
            <img
              src={logo}
              height="30"
              className="d-inline-block align-top ms-2"
              alt="Wareclouds logo"
            />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="wareclouds-nav" />
          <Navbar.Collapse id="wareclouds-nav">
            <Nav className="me-auto">
              {!isIdentityRejected && (
                <Nav.Link
                  as={Link}
                  to="/orders/dashboard"
                  className="text-white"
                >
                  {t('orders', { ns: 'glossary' })}
                </Nav.Link>
              )}
              {showStandardFeatures && (
                <>
                  {!isIdentityRejected && (
                    <NavDropdown
                      title={t('routes', { ns: 'glossary' })}
                      id="route-dropdown"
                    >
                      <NavDropdown.Item as={Link} to="/routes/dashboard">
                        {t('enabled_routes', { ns: 'glossary' })}
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/routes/history">
                        {t('routes_history', { ns: 'glossary' })}
                      </NavDropdown.Item>
                    </NavDropdown>
                  )}

                  <Nav.Link
                    as={Link}
                    to="/routes/optimized"
                    className="text-white"
                  >
                    {t('optimize', { ns: 'glossary' })}
                  </Nav.Link>

                  <Nav.Link
                    as={Link}
                    to="/semi-monthly-summary"
                    className="text-white"
                  >
                    {t('semi_monthly_summary', { ns: 'glossary' })}
                  </Nav.Link>
                </>
              )}
            </Nav>
          </Navbar.Collapse>

          <Navbar.Collapse className="justify-content-end">
            {showStandardFeatures && env.CONTEXT === COUNTRIES.CL && (
              <Nav className="flex-row">
                <NavDropdown
                  title={t('help', { ns: 'glossary' })}
                  id="support-dropdown"
                  className="w-100"
                  align="end"
                >
                  <NavDropdown.Item
                    href={env.USER_FAQ_URL}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {t('faq', { ns: 'glossary' })}
                  </NavDropdown.Item>
                </NavDropdown>
              </Nav>
            )}
            <Nav className="flex-row">
              <NavDropdown
                id="user-menu-dropdown"
                title={username}
                className="w-100"
                align="end"
              >
                {showBankAccount && (
                  <NavDropdown.Item
                    onClick={() => navigate('/profile/bank-account')}
                  >
                    {t('bank_info', { ns: 'glossary' })}
                  </NavDropdown.Item>
                )}
                <NavDropdown.Item onClick={() => setShowLogoutModal(true)}>
                  {t('logout', { ns: 'glossary' })}
                </NavDropdown.Item>
              </NavDropdown>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      {isBlocked && (
        <AlertComponent severity="error" variant="filled">
          {t('blocked_banner', { ns: 'glossary' })}
        </AlertComponent>
      )}
      {showBankAccount && !isBankAccountRoute && <MessageHasNotBankData />}
    </>
  );
};

export default NavigationBar;
